import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API from '../api/axios';

const FOOTER_FALLBACK = {
  quote:
    'St. Andrews World School is dedicated to shaping tomorrow\'s leaders by providing education beyond academics. With six successful branches across India, our mission is to foster curiosity, ignite a passion for learning, and empower students to reach their full potential.',
  navigation: [
    { title: 'About Us', url: '/about-us' },
    { title: 'Infrastructure', url: '/infrastructure' },
    { title: 'Admission', url: '/admission' },
    { title: 'Gallery', url: '/gallery' },
    { title: 'Contact Us', url: '/contact-us' },
  ],
  social: [
    { title: 'Facebook', key: 'facebook', icon: 'fab fa-facebook-f' },
    { title: 'Twitter', key: 'twitter', icon: 'fab fa-x-twitter' },
    { title: 'Instagram', key: 'instagram', icon: 'fab fa-instagram' },
    { title: 'Linkedin', key: 'linkedin', icon: 'fab fa-linkedin-in' },
  ],
  contact: {
    address: 'P.S.-01, Sector-09, Siddharth Vihar Ghaziabad – 201009',
    phone: '9311668863, 9311668864',
    email: 'Info@sawssiddharthvihar.in',
  },
  copyright: 'St. Andrews World School. All right reserved.',
};

const Footer = () => {
  const [settings, setSettings] = useState({});
  const [contacts, setContacts] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingRes, contactRes] = await Promise.all([
          API.get('/settings/public'),
          API.get('/contacts/public')
        ]);
        setSettings(settingRes.data.data || {});
        setContacts(contactRes.data.data || {});
      } catch (err) { console.error(err); }
    };
    fetchData();
  }, []);

  const effectiveNav = FOOTER_FALLBACK.navigation;

  const effectiveSocial = FOOTER_FALLBACK.social;

  const effectiveAddress = contacts.address || FOOTER_FALLBACK.contact.address;

  const socialHref = (key) => {
    const urls = {
      facebook: 'https://www.facebook.com/St-Andrews-World-SchoolSiddharth-Vihar-110403858139470/',
      instagram: 'https://www.instagram.com/sawssiddharthvihar/',
      youtube: 'https://youtube.com/channel/UCN-rxHSuiW_R7rPaTuvb5dw',
    };
    return urls[key] || contacts[key] || '#';
  };

  return (
    <footer className="bg-[#1B3160] text-white/80 pt-14 pb-0 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-secondary/5 blur-3xl"></div>
        <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:32px_32px]"></div>
      </div>
      <div className="container relative">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10 md:gap-12">
          {/* Column 1: About School */}
          <div>
            <h3 className="text-white/80 tracking-[0.14em] uppercase text-[15px] font-semibold">About School</h3>
            <div className="w-10 h-[3px] mt-2.5 mb-4 bg-gradient-to-r from-[#d8a85f] to-[#e8c58b] rounded-full"></div>
            <p className="text-white/90 leading-relaxed">
              {settings.footer_text || FOOTER_FALLBACK.quote}
            </p>
          </div>

          {/* Column 2: Navigation */}
          <div>
            <h3 className="text-white/80 tracking-[0.14em] uppercase text-[15px] font-semibold">Navigation</h3>
            <div className="w-10 h-[3px] mt-2.5 mb-4 bg-gradient-to-r from-[#d8a85f] to-[#e8c58b] rounded-full"></div>
            <ul className="space-y-2.5">
              {effectiveNav.map((m) => (
                <li key={m.url}>
                  <Link to={m.url} className="text-white/90 hover:text-white transition-colors">
                    {m.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Social */}
          <div>
            <h3 className="text-white/80 tracking-[0.14em] uppercase text-[15px] font-semibold">Social Media</h3>
            <div className="w-10 h-[3px] mt-2.5 mb-4 bg-gradient-to-r from-[#d8a85f] to-[#e8c58b] rounded-full"></div>
            <ul className="space-y-2.5">
              {effectiveSocial.map((s) => (
                <li key={s.key}>
                  <a
                    href={socialHref(s.key)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/90 hover:text-white transition-colors inline-flex items-center gap-2"
                    aria-label={s.title}
                  >
                    <i className={`${s.icon} text-[15px] w-5 text-center`} aria-hidden />
                    <span>{s.title}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact */}
          <div>
            <h3 className="text-white/80 tracking-[0.14em] uppercase text-[15px] font-semibold">Contact Us</h3>
            <div className="w-10 h-[3px] mt-2.5 mb-4 bg-gradient-to-r from-[#d8a85f] to-[#e8c58b] rounded-full"></div>
            <div className="space-y-3 text-white/90 leading-relaxed">
              <p>
                <span className="text-white/60 text-[15px] uppercase tracking-wider block mb-0.5">Address</span>
                {effectiveAddress}
              </p>
              <div>
                <span className="text-white/60 text-[15px] uppercase tracking-wider block mb-0.5">Phone</span>
                <a href="tel:+919311668863" className="text-white/90 hover:text-white transition-colors block">+91-9311668863</a>
                <a href="tel:+919311668864" className="text-white/90 hover:text-white transition-colors block">+91-9311668864</a>
              </div>
              <div>
                <span className="text-white/60 text-[15px] uppercase tracking-wider block mb-0.5">Email</span>
                <a href="mailto:Info@sawssiddharthvihar.in" className="text-white/90 hover:text-white transition-colors">Info@sawssiddharthvihar.in</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-12 border-t border-white/5">
        <div className="container py-6">
          <div className="lg:hidden flex justify-center mb-4">
            <img src="/images/cambridge-logo.jpeg" alt="Cambridge" className="h-16 w-auto" />
          </div>
          <div className="text-sm text-white/40 text-center">
            {settings.footer_credit || FOOTER_FALLBACK.copyright}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
